/**
 * Plans Slice
 * Manages plan state and async operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { plansApi } from '../../utils/api/plans.js';
import { getErrorMessage } from '../../utils/api/errors.js';

/**
 * Initial state
 */
const initialState = {
  plans: [],
  currentPlan: null,
  stats: null,
  loading: false,
  listLoading: false,
  detailLoading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    is_active: null,
    search: '',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * Async thunk: Fetch all plans
 */
export const fetchPlans = createAsyncThunk(
  'plans/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await plansApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch plans'));
    }
  }
);

/**
 * Async thunk: Fetch plan by ID
 */
export const fetchPlanById = createAsyncThunk(
  'plans/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await plansApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch plan'));
    }
  }
);

/**
 * Async thunk: Fetch active plans
 */
export const fetchActivePlans = createAsyncThunk(
  'plans/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await plansApi.getActive();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch active plans'));
    }
  }
);

/**
 * Async thunk: Create plan
 */
export const createPlan = createAsyncThunk(
  'plans/create',
  async (planData, { rejectWithValue }) => {
    try {
      const response = await plansApi.create(planData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create plan'));
    }
  }
);

/**
 * Async thunk: Update plan
 */
export const updatePlan = createAsyncThunk(
  'plans/update',
  async ({ id, planData }, { rejectWithValue }) => {
    try {
      const response = await plansApi.update(id, planData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update plan'));
    }
  }
);

/**
 * Async thunk: Delete plan
 */
export const deletePlan = createAsyncThunk(
  'plans/delete',
  async (id, { rejectWithValue }) => {
    try {
      await plansApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete plan'));
    }
  }
);

/**
 * Async thunk: Toggle plan active status
 */
export const togglePlanActive = createAsyncThunk(
  'plans/toggleActive',
  async (id, { rejectWithValue }) => {
    try {
      const response = await plansApi.toggleActive(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to toggle plan status'));
    }
  }
);

/**
 * Async thunk: Fetch plan statistics
 */
export const fetchPlanStats = createAsyncThunk(
  'plans/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await plansApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

/**
 * Plans slice
 */
const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {
    /**
     * Clear current plan
     */
    clearCurrentPlan: (state) => {
      state.currentPlan = null;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Set pagination
     */
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all plans
      .addCase(fetchPlans.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.listLoading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.plans = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
        state.plans = [];
      })

      // Fetch plan by ID (must not toggle list loading — that remounts the page behind Edit)
      .addCase(fetchPlanById.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchPlanById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentPlan = action.payload;
      })
      .addCase(fetchPlanById.rejected, (state) => {
        state.detailLoading = false;
        state.currentPlan = null;
      })

      // Fetch active plans
      .addCase(fetchActivePlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivePlans.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.plans = action.payload;
        } else if (action.payload?.data && Array.isArray(action.payload.data)) {
          state.plans = action.payload.data;
        } else {
          state.plans = [];
        }
      })
      .addCase(fetchActivePlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.plans = [];
      })

      // Create plan
      .addCase(createPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plans.unshift(action.payload);
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update plan
      .addCase(updatePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
        if (state.currentPlan?.id === action.payload.id) {
          state.currentPlan = action.payload;
        }
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete plan
      .addCase(deletePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = state.plans.filter((p) => p.id !== action.payload);
        if (state.currentPlan?.id === action.payload) {
          state.currentPlan = null;
        }
      })
      .addCase(deletePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle plan active
      .addCase(togglePlanActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(togglePlanActive.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
        if (state.currentPlan?.id === action.payload.id) {
          state.currentPlan = action.payload;
        }
      })
      .addCase(togglePlanActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch statistics
      .addCase(fetchPlanStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchPlanStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentPlan,
  clearError,
  setFilters,
  resetFilters,
  setPagination,
} = plansSlice.actions;

export default plansSlice.reducer;
